import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, LogOut, Crown, Upload, Search, } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../lib/constants';

export const DashboardHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const premiumPlans = ['weekly', 'monthly', 'semester'];
  const isPremium = premiumPlans.includes(profile?.subscription_plan?.toLowerCase() || 'free');

  return (
    <header className="sticky top-0 z-50 backdrop-blur-lg bg-white/70 border-b border-cream-300/40 shadow-sm px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex justify-between items-center h-14 sm:h-16">
        
        {/* LEFT SIDE: Brand Core Identity */}
        <button 
          onClick={() => navigate(ROUTES.DASHBOARD)}
          className="flex items-center space-x-2 sm:space-x-2.5 hover:opacity-80 transition-opacity active:scale-98"
        >
          <div className="bg-gradient-to-br from-brown-600 to-brown-700 p-1.5 rounded-lg shadow-md aspect-square w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center overflow-hidden border border-brown-400/20">
            <img src="/book.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <span className="text-base sm:text-lg font-bold tracking-tight text-brown-900">
            OmniClarix
          </span>
        </button>

        {/* CENTER VIEW NAVIGATION LINKS: (Only visible on Desktop/Widescreen panels) */}
        <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              location.pathname === ROUTES.DASHBOARD ? 'bg-brown-100/60 text-brown-900 font-semibold' : 'text-brown-600 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => navigate(ROUTES.HOW_IT_WORKS)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              location.pathname === ROUTES.HOW_IT_WORKS ? 'bg-brown-100/60 text-brown-900 font-semibold' : 'text-brown-600 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
          >
            How It Works
          </button>
          <button
            onClick={() => navigate(ROUTES.UPLOAD)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center space-x-1.5 ${
              location.pathname === ROUTES.UPLOAD ? 'bg-brown-100/60 text-brown-900 font-semibold' : 'text-brown-600 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>Upload</span>
          </button>
          <button
            onClick={() => navigate(ROUTES.PAST_QUESTIONS)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center space-x-1.5 ${
              location.pathname === ROUTES.PAST_QUESTIONS ? 'bg-brown-100/60 text-brown-900 font-semibold' : 'text-brown-600 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
          >
            <Search className="w-4 h-4" />
            <span>Past Questions</span>
          </button>
          <button
            onClick={() => navigate(ROUTES.HISTORY)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              location.pathname === ROUTES.HISTORY ? 'bg-brown-100/60 text-brown-900 font-semibold' : 'text-brown-600 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
          >
            History
          </button>
        </nav>

        {/* RIGHT SIDE: Action Utility Tray */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          {/* Responsive Premium Badge */}
          {isPremium && (
            <span className="px-2.5 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] font-black rounded-full shadow-sm flex items-center space-x-1 mr-1">
              <Crown className="w-2.5 h-2.5" />
              <span className="hidden sm:inline">PREMIUM</span>
            </span>
          )}

          {/* Settings Hook (Always Available on both Mobile and Desktop) */}
          <button
            onClick={() => navigate(ROUTES.SETTINGS)}
            className={`p-2 rounded-lg transition-colors ${
              location.pathname === ROUTES.SETTINGS ? 'bg-brown-100/60 text-brown-900' : 'text-brown-500 hover:text-brown-900 hover:bg-cream-100/40'
            }`}
            title="Settings"
          >
            <Settings className="w-4 h-4 stroke-[2.2]" />
          </button>

          {/* Logout Action Button (Automatically hidden on Mobile, handled inside Settings) */}
          <button
            onClick={handleLogout}
            className="hidden md:block p-2 text-brown-400 hover:text-red-600 hover:bg-red-50/60 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4 stroke-[2.2]" />
          </button>
        </div>

      </div>
    </header>
  );
};

export default DashboardHeader;